// Office Allowances page (Session AAW, Loop 4). A per-contract worklist of how much of each active
// contract's inclusive tier allowance it has used THIS contract-year: Care GBP250 / Complete GBP400 of
// parts per lift, and Complete's six inclusive call-outs. A contract that has gone over is flagged in
// red so the office bills the excess by hand (nothing bills it automatically). Read-only: the figures
// are derived from the parts and call-outs already recorded, never stored.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { ContractAllowanceRow } from "../../lib/api";
import { SkeletonRows } from "../../components/Skeleton";
import { StatusPill, type PillTone } from "../../components/StatusPill";
import { PageHeader } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };

function money(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// The row's overall allowance status: over (red), within (green) or none-included (muted for Core /
// an unprovisioned site with no inclusive allowance at all). Colour reinforces the word, never alone.
function rowStatus(row: ContractAllowanceRow): { word: string; tone: PillTone } {
  if (row.overLimit) {
    return { word: "Over allowance", tone: "red" };
  }
  if (row.parts.hasAllowance || row.callOuts.hasAllowance) {
    return { word: "Within allowance", tone: "green" };
  }
  return { word: "No inclusive allowance", tone: "grey" };
}

// M-10, Session AQU: search and sort. Search matches what the office would type (customer, site,
// tier); sort takes numbers as numbers. With no sort column chosen the page keeps its own default,
// over-limit contracts first and then the server order.
const ALLOWANCE_SEARCH_ACCESSORS: SearchAccessors<ContractAllowanceRow> = [
  (r) => r.customerName,
  (r) => r.siteName,
  (r) => TIER_LABEL[r.tier] ?? r.tier,
];
const TIER_RANK: Record<string, number> = { core: 0, care: 1, complete: 2 };
// Status sorts by severity: no inclusive allowance, within, then over.
function statusRank(r: ContractAllowanceRow): number {
  if (r.overLimit) return 2;
  return r.parts.hasAllowance || r.callOuts.hasAllowance ? 1 : 0;
}
const ALLOWANCE_SORT_ACCESSORS: SortAccessors<ContractAllowanceRow> = {
  customer: (r) => r.customerName,
  site: (r) => r.siteName,
  tier: (r) => TIER_RANK[r.tier] ?? r.tier,
  items: (r) => r.coveredItemCount,
  // A contract with no inclusive parts or call-outs sorts as a blank (last), as it reads.
  parts: (r) => (r.parts.hasAllowance ? r.parts.consumed : null),
  callOuts: (r) => (r.callOuts.hasAllowance ? r.callOuts.used : null),
  status: (r) => statusRank(r),
};

export function AllowancesPage() {
  const [rows, setRows] = useState<ContractAllowanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [overOnly, setOverOnly] = useState(false);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("allowances");
  // Below md the contracts render as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  function load() {
    setLoading(true);
    setError(false);
    return api.contracts
      .allowances()
      .then((r) => setRows(r))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    api.contracts
      .allowances()
      .then((r) => live && setRows(r))
      .catch(() => live && setError(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, []);

  const overCount = rows.filter((r) => r.overLimit).length;
  // Over-limit contracts first (the ones that need billing), then the server order (newest active).
  const sorted = useMemo(() => {
    const base = overOnly ? rows.filter((r) => r.overLimit) : rows;
    return [...base].sort((a, b) => Number(b.overLimit) - Number(a.overLimit));
  }, [rows, overOnly]);
  const shown = sortRows(filterRows(sorted, search, ALLOWANCE_SEARCH_ACCESSORS), ALLOWANCE_SORT_ACCESSORS, sort);

  return (
    <div>
      <PageHeader
        title="Allowances"
        className="mb-4"
        subtitle={
          <>
            Inclusive parts and call-outs used against each active contract this contract-year. Care includes up
            to {money(250)} of parts per lift a year, Complete up to {money(400)} plus six call-outs. A contract
            over its allowance is flagged so you can bill the excess by hand.{" "}
            {overCount > 0
              ? `${overCount} ${overCount === 1 ? "contract is" : "contracts are"} over the allowance.`
              : "None are over the allowance."}
          </>
        }
      />

      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search customer, site or tier..."
        searchLabel="Search allowances"
        filters={
          <button
            type="button"
            aria-pressed={overOnly}
            onClick={() => setOverOnly((v) => !v)}
            className={`rounded-full border px-3 py-1 font-menu text-xs tracking-wide ${
              overOnly ? "border-ele-danger text-ele-danger" : "border-ele-purple-accent text-ele-body"
            }`}
          >
            Over allowance only
          </button>
        }
      />

      {loading ? (
        <div role="status">
          <span className="sr-only">Loading the contract allowances</span>
          <SkeletonRows rows={6} />
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong"
        >
          <p>Could not load the allowances. Please try again in a moment.</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-1.5 text-sm text-ele-blue"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="font-heading text-lg text-ele-strong">No active contracts</p>
          <p className="mt-1 text-sm text-ele-body">
            Allowances appear here once a contract is active. Core contracts carry no inclusive parts or call-outs.
          </p>
        </div>
      ) : sorted.length === 0 ? (
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">
          No contracts are over their allowance.
        </p>
      ) : shown.length === 0 ? (
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">
          No contracts match &quot;{search.trim()}&quot;.
        </p>
      ) : mobileCards ? (
        /* Below md the seven columns cannot share 375px. Each contract is a card: the customer and
           site lead, the tier and covered items the table hid follow, and the parts and call-out
           usage are shown in full under them. The pill still carries over or within allowance. */
        <MobileCardList label="Contract allowances">
          {shown.map((r) => {
            const status = rowStatus(r);
            return (
              <MobileListCard
                key={r.contractId}
                to={`/office/contracts/${r.contractId}`}
                primary={r.customerName}
                secondary={r.siteName}
                meta={
                  <>
                    <span className="block">
                      {TIER_LABEL[r.tier] ?? r.tier} · {r.coveredItemCount === 1 ? "1 covered item" : `${r.coveredItemCount} covered items`}
                    </span>
                    {r.contractYear == null && <span className="block">No contract-year anchor (all-time)</span>}
                  </>
                }
                right={<StatusPill word={status.word} tone={status.tone} />}
                details={
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="font-menu text-xs uppercase tracking-wide text-ele-body">Parts (this year)</dt>
                      <dd>
                        {r.parts.hasAllowance ? (
                          <>
                            <span className="text-ele-strong">
                              {money(r.parts.consumed)} of {money(r.parts.allowance)}
                            </span>
                            <span className={`block text-xs ${r.parts.remaining < 0 ? "text-ele-danger" : "text-ele-body"}`}>
                              {r.parts.remaining < 0 ? `${money(-r.parts.remaining)} over` : `${money(r.parts.remaining)} left`}
                            </span>
                          </>
                        ) : (
                          <span className="text-ele-body opacity-60">No inclusive parts</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-menu text-xs uppercase tracking-wide text-ele-body">Call-outs</dt>
                      <dd>
                        {r.callOuts.hasAllowance ? (
                          <>
                            <span className="text-ele-strong">
                              {r.callOuts.used} of {r.callOuts.included}
                            </span>
                            <span className={`block text-xs ${r.callOuts.remaining < 0 ? "text-ele-danger" : "text-ele-body"}`}>
                              {r.callOuts.remaining < 0 ? `${-r.callOuts.remaining} over` : `${r.callOuts.remaining} left`}
                            </span>
                          </>
                        ) : (
                          <span className="text-ele-body opacity-60">None included</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                }
              />
            );
          })}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "18%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "9%" }} />
              <col className="max-md:hidden" style={{ width: "7%" }} />
              <col style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Tier" colKey="tier" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Covered items" colKey="items" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                <SortableHeader label="Parts (this year)" colKey="parts" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Call-outs" colKey="callOuts" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const status = rowStatus(r);
                return (
                  <tr key={r.contractId} className={`border-t border-ele-purple-accent ${r.overLimit ? "bg-ele-danger/10" : ""}`}>
                    <td className="px-3 py-2 align-top text-ele-strong">
                      <Link to={`/office/contracts/${r.contractId}`} className="hover:text-ele-green hover:underline">
                        {r.customerName}
                      </Link>
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      {r.siteName}
                      {r.contractYear == null && (
                        <div className="text-[11px] text-ele-body opacity-60">No contract-year anchor (all-time)</div>
                      )}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">{TIER_LABEL[r.tier] ?? r.tier}</td>
                    <td className="max-md:hidden px-3 py-2 text-right align-top">{r.coveredItemCount}</td>
                    <td className="px-3 py-2 align-top">
                      {r.parts.hasAllowance ? (
                        <div>
                          <div className="text-ele-strong">
                            {money(r.parts.consumed)} of {money(r.parts.allowance)}
                          </div>
                          <div className={`text-xs ${r.parts.remaining < 0 ? "text-ele-danger" : "text-ele-body"}`}>
                            {r.parts.remaining < 0
                              ? `${money(-r.parts.remaining)} over`
                              : `${money(r.parts.remaining)} left`}
                          </div>
                        </div>
                      ) : (
                        <span className="text-ele-body opacity-60">No inclusive parts</span>
                      )}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      {r.callOuts.hasAllowance ? (
                        <div>
                          <div className="text-ele-strong">
                            {r.callOuts.used} of {r.callOuts.included}
                          </div>
                          <div className={`text-xs ${r.callOuts.remaining < 0 ? "text-ele-danger" : "text-ele-body"}`}>
                            {r.callOuts.remaining < 0
                              ? `${-r.callOuts.remaining} over`
                              : `${r.callOuts.remaining} left`}
                          </div>
                        </div>
                      ) : (
                        <span className="text-ele-body opacity-60">None included</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill word={status.word} tone={status.tone} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
